import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { NotificationService } from '@app/core/services/notification.service'
import { LoadingService } from '@app/core/state/loading/loading.service'
import { FormSection } from '@app/shared/form/form-section'
import type { Option } from '@app/shared/form/input-base'
import { InputDropdown } from '@app/shared/form/input-dropdown'
import { InputTextbox } from '@app/shared/form/input-textbox'
import type { Footer, MeasurementUnitType, Settings, UpdateSettings } from '@app/user/state/user.model'
import { UserQuery } from '@app/user/state/user.query'
import { UserService } from '@app/user/state/user.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Observable } from 'rxjs'
import { toFormGroup } from '@app/shared/form/form-input.service'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'

interface UserSettingsFormState {
  general: {
    uiLanguage: string
    printoutLanguage: string
    marketArea: string
    sharingGroup: number | string
    showMinMaxAirflow: string
  }
  measurementUnits: Record<string, string>
  footer: Footer
}

@UntilDestroy()
@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserSettingsComponent implements OnInit {
  isLoading$: Observable<boolean>

  isFormSubmitted: boolean

  formSections: FormSection[] = []

  currentFormState: UserSettingsFormState

  form: UntypedFormGroup

  constructor(
    private userService: UserService,
    private userQuery: UserQuery,
    private loadingService: LoadingService,
    private dialogRef: MatDialogRef<UserSettingsComponent>,
    private notificationService: NotificationService,
    private formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.isFormSubmitted = false
    this.isLoading$ = this.loadingService.isLoading$

    this.userQuery.settings$.pipe(untilDestroyed(this)).subscribe({
      next: settings => {
        if (!settings) {
          return
        }
        this.formSections = this.buildForm(settings)
        this.currentFormState = this.constructFormState(settings)
        const groups: Record<string, UntypedFormGroup> = {}
        this.formSections.forEach(section => {
          groups[section.key] = toFormGroup(section.inputs)
        })
        this.form = this.formBuilder.group(groups)
        this.form.patchValue(this.currentFormState, { emitEvent: false })
      },
    })

    if (!this.userQuery.hasSettings) {
      this.userService.getSettings().pipe(untilDestroyed(this)).subscribe()
    }
  }

  onSubmit(event: UserSettingsFormState): void {
    this.isFormSubmitted = true
    const { general, measurementUnits, footer } = event

    const settings = {
      DefaultSharingProject: Number(general.sharingGroup),
      MarketArea: general.marketArea,
      UILanguage: general.uiLanguage,
      PrintoutLanguage: general.printoutLanguage,
      Footer: footer,
      MeasurementUnitTypes: measurementUnits,
      ShowMinMaxAirflow: general.showMinMaxAirflow,
    } as UpdateSettings

    this.userService
      .saveSettings(settings)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.dialogRef.close()
          this.notificationService.success('UI.Settings.Saved')
        },
      })
  }

  onCancel(): void {
    this.dialogRef.close()
  }

  buildForm = (settings: Settings): FormSection[] => {
    const generalSection = this.getGeneralSection(settings)
    const measurementUnitSection = this.getMeasurementUnitSection(settings.MeasurementUnitTypes)
    const footerSection = this.getFooterSection(settings.Footer)
    return [generalSection, measurementUnitSection, footerSection]
  }

  constructFormState = ({
    UILanguage,
    PrintoutLanguage,
    MarketArea,
    DefaultShareProject,
    ShowMinMaxAirflow,
    MeasurementUnitTypes,
    Footer: footer,
  }: Settings): UserSettingsFormState => {
    const selectedUnits: Record<string, string> = {}

    MeasurementUnitTypes.forEach(type => {
      selectedUnits[type.Name] = type.SelectedUnitKey
    })

    return {
      general: {
        uiLanguage: UILanguage,
        printoutLanguage: PrintoutLanguage,
        marketArea: MarketArea,
        sharingGroup: DefaultShareProject ?? -1,
        showMinMaxAirflow: ShowMinMaxAirflow,
      },
      measurementUnits: {
        ...selectedUnits,
      },
      footer: { ...footer },
    }
  }

  getGeneralSection = ({ Languages, MarketAreas, UserGroups }: Settings): FormSection => {
    const uiLanguage = new InputDropdown({
      key: 'uiLanguage',
      label: 'UI.UILanguage',
      options: Languages.map<Option>(language => ({
        key: language.Code,
        value: language.TitleTextId,
      })),
    })

    const printoutLanguage = new InputDropdown({
      key: 'printoutLanguage',
      label: 'UI.PrintoutLanguage',
      options: Languages.map<Option>(language => ({
        key: language.Code,
        value: language.TitleTextId,
      })),
    })

    const marketArea = new InputDropdown({
      key: 'marketArea',
      label: 'UI.MarketAreas',
      options: MarketAreas.map<Option>(area => ({
        key: area.Code,
        value: area.TitleTextId,
      })),
    })

    const sharingGroup = new InputDropdown({
      key: 'sharingGroup',
      label: 'UI.DefaultSharingGroup',
      options: UserGroups.map<Option>(group => ({
        key: group.Id,
        value: group.Name,
      })),
    })

    const minMaxAirflow = new InputDropdown({
      key: 'showMinMaxAirflow',
      label: 'UI.showMinMaxAirflow',
      options: [
        { key: 'Yes', value: 'Yes' },
        { key: 'No', value: 'No' },
      ],
    })

    return new FormSection('general', [uiLanguage, printoutLanguage, marketArea, sharingGroup, minMaxAirflow], { label: 'UI.General' })
  }

  getMeasurementUnitSection = (measurementUnitTypes: MeasurementUnitType[]): FormSection => {
    const inputs: InputDropdown[] = []

    measurementUnitTypes.forEach(type => {
      inputs.push(
        new InputDropdown({
          key: type.Name,
          label: type.TitleTextId,
          options: type.Units.map<Option>(unit => ({
            key: unit.UnitKey,
            value: unit.UnitTitleTextId,
          })),
        }),
      )
    })

    return new FormSection('measurementUnits', inputs, {
      label: 'UI.MeasurementUnitType',
    })
  }

  getFooterSection = (footer: Footer): FormSection => {
    const inputs: InputTextbox[] = []

    Object.keys(footer).forEach(key => {
      inputs.push(
        new InputTextbox({
          key,
          label: key,
        }),
      )
    })

    return new FormSection('footer', inputs, {
      label: 'UI.Footer',
    })
  }
}
